import type { AppState } from 'types/app';
import type { AppDispatch } from 'entrypoints/bootstrapOnClient';
import { RECEIVE_CUSTOMER_AUTH_DETAILS, RECEIVE_CUSTOMER_SOFT_AUTH } from 'constants/reduxActions';
import { err, setError } from 'actions/errors';
import { redirectToAuthenticationFor } from 'actions/redirect';
import { setSessionCookies } from 'actions/session';
import { getCustomerAuthentication } from 'apis/mafia';
import { parsePath } from 'helpers/LocationUtils';
import { processHeadersMiddleware } from 'middleware/processHeadersMiddlewareFactory';
import { fetchErrorMiddleware } from 'middleware/fetchErrorMiddleware';
import { selectMafiaConfig } from 'selectors/environment';
import { EMAIL_TOKEN_COOKIE } from 'constants/cookies';

interface CustomerAuthResponse {
  success: boolean;
}

export function receiveCustomerAuthDetails(response: CustomerAuthResponse) {
  return {
    type: RECEIVE_CUSTOMER_AUTH_DETAILS,
    authObj: response
  };
}

export function receiveCustomerRecognized(isRecognizedCustomer: boolean) {
  return {
    type: RECEIVE_CUSTOMER_SOFT_AUTH,
    isRecognizedCustomer
  };
}

export function fetchCustomerAuthDetails(customerAuthFetcher = getCustomerAuthentication) {
  return (dispatch: AppDispatch, getState: () => AppState) => {
    const state = getState();
    const { cookies } = getState();
    const mafiaConfig = selectMafiaConfig(state);

    return customerAuthFetcher(mafiaConfig, cookies)
      .then(processHeadersMiddleware(setSessionCookies(dispatch, getState)))
      .then(fetchErrorMiddleware)
      .then((response: CustomerAuthResponse) => {
        dispatch(receiveCustomerAuthDetails(response));
        return response;
      })
      .catch((e: any) => {
        dispatch(setError(err.GENERIC, e));
        return { success: false }; // emulate the response failure in case any caller is explicitly .then'ing the promise.
      });
  };
}

interface CheckAuthenticationParams {
  callbackIfNotAuthenticated?: () => void;
  isEmailTokenAuthorized?: boolean;
  redirectOnClose?: string;
  fetcherOfCustomerAuthDetails?: () => void;
}

export function checkCustomerAuthentication(
  urlToReturnToOnFailure: string,
  callbackIfAuthenticated: () => void,
  {
    callbackIfNotAuthenticated = () => {},
    isEmailTokenAuthorized = false,
    redirectOnClose,
    fetcherOfCustomerAuthDetails = fetchCustomerAuthDetails
  }: CheckAuthenticationParams = {} as CheckAuthenticationParams
) {
  return (dispatch: AppDispatch, getState: () => AppState) => {
    const redirectLocation = typeof urlToReturnToOnFailure === 'object' ? urlToReturnToOnFailure : parsePath(urlToReturnToOnFailure);
    const { cookies, authentication, customer: isRecognizedCustomer } = getState();

    if (authentication === true || (isEmailTokenAuthorized && cookies[EMAIL_TOKEN_COOKIE])) {
      callbackIfAuthenticated();
      return Promise.resolve();
    } else if (!isRecognizedCustomer || authentication === false) {
      // TODO these redirect calls should have the option to be "push" rather than replace style once https://github.zappos.amazon.dev/mweb/marty/issues/3360 is implemented
      callbackIfNotAuthenticated();
      return dispatch(redirectToAuthenticationFor(redirectLocation, redirectOnClose));
    } else {
      // user is recognized and we have not checked auth yet so we need to ask nicely the authentication status
      return dispatch(fetcherOfCustomerAuthDetails()).then((result: any) => {
        if (result.success) {
          callbackIfAuthenticated();
        } else {
          callbackIfNotAuthenticated?.();
          return dispatch(redirectToAuthenticationFor(redirectLocation, redirectOnClose));
        }
      });
    }
  };
}
